import TrapFocus from '../utils/trap-focus';

/**
 * Submenu class to manage individual submenu functionality.
 * Handles opening and closing of the submenu, including trap focus.
 * @param {HTMLElement} el - The element that contains the submenu and its button.
 * @param {HTMLElement} siteNav - The root element of the site navigation.
 * @returns {void}
 */
class Submenu {
  constructor(el, siteNav) {
    this.parent = el;
    this.el = el.querySelector('.js-site-nav-submenu');
    this.button = el.querySelector('button');
    this.siteNav = siteNav;

    this.navOpenClass = 'nav-open';
    this.submenuOpenClass = 'submenu-open';

    this.trapFocus = new TrapFocus(this.parent, this.handleEscape.bind(this));

    this.bindEvents();
  }

  /**
   * Bind event listeners for the submenu.
   * @returns {void}
   */
  bindEvents() {
    if (!this.el || !this.button) return;

    this.button.addEventListener('click', this.toggle.bind(this));
  }

  /**
   * Toggle a menu open or closed.
   */
  toggle() {
    const menuToggledEvent = new Event('menu-toggled', { bubbles: true });
    this.button.dispatchEvent(menuToggledEvent);

    if (this.parent.classList.contains(this.submenuOpenClass)) {
      this.close();
      this.trapFocus.deactivate();
    } else {
      this.open();
      this.trapFocus.activate();
    }
  }

  /**
   * Close a given menu and update its button state.
   * @return {void}
   */
  close() {
    this.siteNav.classList.remove(this.navOpenClass);
    this.parent.classList.remove(this.submenuOpenClass);
    this.button.setAttribute('aria-expanded', 'false');
  }

  /**
   * Open a given menu and update its button state.
   * @return {void}
   */
  open() {
    this.siteNav.classList.add(this.navOpenClass);
    this.parent.classList.add(this.submenuOpenClass);
    this.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Handle escape callback to pass to TrapFocus.
   * @return {void}
   */
  handleEscape() {
    if (this.parent.classList.contains(this.submenuOpenClass)) {
      this.close();
      this.button.focus();
    }
  }
}

/**
 * SiteNav class to manage site navigation menus.
 * Handles opening and closing of menus, including a special
 * trap focus functionality for the Departments submenu.
 *
 * @param {HTMLElement} el - The root element of the site navigation.
 * @returns {void}
 */
class SiteNav {
  constructor(el) {
    this.siteNav = el;
    this.screen = el.querySelector('.js-site-nav-screen');
    this.navItems = el.querySelectorAll('.js-site-nav-menu-item');

    this.submenus = [];

    this.init();
    this.bindEvents();
  }

  /**
   * Initialize the SiteNav by creating Submenu instances for each nav item.
   * @returns {void}
   */
  init() {
    this.navItems.forEach((item) => {
      this.submenus.push(new Submenu(item, this.siteNav));
    });
  }

  /**
   * Bind event listeners for the site navigation.
   * @returns {void}
   */
  bindEvents() {
    this.screen.addEventListener('click', this.closeAllMenus.bind(this));
    this.siteNav.addEventListener(
      'menu-toggled',
      this.handleToggledSubmenu.bind(this),
    );
  }

  /**
   * Handle toggling of submenus.
   * @param {Event} e
   * @return {void}
   */
  handleToggledSubmenu(e) {
    if (e.target.classList.contains('site-nav__menu-button')) {
      const submenu = this.submenus.find((sub) => sub.button === e.target);
      if (submenu) {
        this.closeAllMenus(submenu);
      }
    }
  }

  /**
   * Close all menus.
   * If a current menu is provided, close all menus but the current menu
   * @param {HTMLElement} currentMenu - The menu to keep open (optional)
   * @return {void}
   */
  closeAllMenus(currentMenu = null) {
    this.submenus.forEach((submenu) => {
      if (currentMenu) {
        if (submenu !== currentMenu) {
          submenu.close();
        }
      } else {
        submenu.close();
      }
    });
  }
}

export default SiteNav;
